import React, { useState, useEffect } from "react";
import Select from "react-select";
import { connect } from "react-redux";
import { Actions as ASSETACTIONS } from "../../store/actions/assetActions";
import DatePicker from "react-datepicker";
import { addYears, subYears } from "date-fns";
import Tooltip from "@/tooltip";
import Spinner from "../common/Spinner";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { NotificationManager } from "react-notifications";
import { validateFloat } from "../common/validation/Validators";

const assetTypes = [
  { label: "Living Accommodation", value: "Living Accommodation" },
  { label: "Vehicle", value: "Vehicle" },
  { label: "Other", value: "Other" },
];

const subTypes = [
  { label: "Car", value: "Car", type: "Vehicle" },
  { label: "Van", value: "Van", type: "Vehicle" },
  { label: "Motorcycle", value: "Motorcycle", type: "Vehicle" },
  { label: "Quad Bike", value: "Quad Bike", type: "Vehicle" },
  // { label: "Yacht", value: "Yacht", type: "Vehicle" },
  // { label: "Plane", value: "Plane", type: "Vehicle" },
  { label: "Land", value: "Land", type: "Living Accommodation" },
  { label: "House", value: "House", type: "Living Accommodation" },
  // { label: "Laptop", value: "Laptop", type: "Other" },
  // { label: "Clothing", value: "Clothing", type: "Other" },
  // { label: "Fridge", value: "Fridge", type: "Other" },
];

const engineTypes = [
  { label: "1400cc or less", value: "1400cc or less" },
  { label: "1401cc - 2000cc", value: "1401cc - 2000cc" },
  { label: "2001cc +", value: "2001cc +" },
  { label: "Unrecognised Engine Capacity", value: "Unrecognised Engine Capacity" },
  { label: "Pure Electric", value: "Pure Electric" },
];

const fuelTypes = [
  { label: "Diesel (Euro 6d Standard)", value: "F" },
  { label: "Other Diesel", value: "D" },
  { label: "Other", value: "A" },
];

const AssetDetails = ({
  asset,
  saveAsset,
  disabled,
  saving,
  edit,
}) => {
  const [formData, setFormData] = useState({})

  const updateField = ({ subfield, name, value }) => {
    if (subfield) {
      setFormData({
        ...formData,
        [subfield]: {
          ...[subfield],
          [name]: value
        }
      })
      return
    }
    setFormData({
      ...formData,
      [name]: value
    })
  }

  const onSubmit = (event) => {
    event.preventDefault()

    if (!formData.name || !formData.type || !formData.cost || !formData.cashEquivalent) {
      NotificationManager.error('Please ensure all fields are filled', 'Saving Asset')
      return
    }

    switch (formData?.type) {
      case 'Living Accommodation':
        break
      case 'Vehicle':
        if (!formData.engineType || !formData.fuelType || !formData.make || !formData.model || !formData.registeredDate) {
          NotificationManager.error('Please ensure all fields are filled', 'Saving Asset')
          return
        }
        break
    }

    saveAsset(formData);
  };

  useEffect(() => {
    if (!asset?.id) return

    setFormData(asset)

  }, [asset])

  return (
    <form onSubmit={onSubmit}>
      <div className="col-lg-12 col-sm-12 form-section">
        <div className='col-lg-12 col-sm-12 section-header'>
          <h1>
            Asset Details
          </h1>
        </div>

        <div className="col-sm-3">
          <div className="form-material form-container">
            <input
              className="form-control form-input"
              id="name"
              name="name"
              value={formData?.name}
              disabled={disabled}
              onChange={(e) => {
                updateField({ name: e.target.name, value: e.target.value })
              }}
              autoComplete='off'
            />
            <label htmlFor="name">Name*</label>
          </div>
        </div>

        <div className="col-sm-3 pr-0">
          <div className="form-material form-container">
            <input
              className="form-control form-input"
              id="reference"
              name="reference"
              value={formData?.reference}
              disabled={disabled}
              onChange={(e) => {
                updateField({ name: e.target.name, value: e.target.value })
              }}
              autoComplete='off'
            />
            <label htmlFor="reference">Reference</label>
          </div>
        </div>

        <div className="col-sm-3 pr-0">
          <div className="form-material form-container">
            <label htmlFor="type">Type*</label>
            <Select
              isDisabled={disabled}
              id="type"
              name="type"
              options={assetTypes}
              value={assetTypes?.find(item => item.value === formData?.type)}
              onChange={(selected) => {
                updateField({ name: 'type', value: selected?.value })
              }}
            />
          </div>
        </div>

        <div className="col-sm-3 pr-0">
          <div className="form-material form-container">
            <label htmlFor="subType">Subtype</label>
            <Select
              isDisabled={disabled}
              id="subType"
              name="subType"
              options={subTypes?.filter((subType) => subType.type === formData?.type)}
              value={subTypes?.find(item => item.value === formData?.subType)}
              onChange={(selected) => {
                updateField({ name: 'subType', value: selected?.value })
              }}
            />
          </div>
        </div>

        <div className="col-sm-12 pr-0">
          <div className="form-material form-container">
            <textarea
              className="form-input"
              id="description"
              name="description"
              value={formData?.description}
              disabled={disabled}
              onChange={(e) => {
                updateField({ name: e.target.name, value: e.target.value })
              }}
              autoComplete='off'
            />
            <label htmlFor="description">Description</label>
          </div>
        </div>

        <div className="col-sm-3 pr-0">
          <label className="css-input switch switch-sm switch-success" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <input
              type="checkbox"
              id="isExempt"
              name="isExempt"
              disabled={disabled}
              autoComplete='off'
              onChange={(e) => {
                updateField({ name: e.target.name, value: e.target.value })
              }}
              defaultChecked={formData?.isExempt}
            />
            <span></span>
            <Tooltip content="Only tick this checkbox if this asset should be ignored from payroll">
              <label htmlFor="isExempt" style={{ marginBottom: 0 }}>
                Exempt From Payroll
              </label>
            </Tooltip>
          </label>
        </div>

        <div className="col-sm-3 pr-0">
          <div className="form-material form-container">
            <input
              className="form-control"
              id="cost"
              name="cost"
              value={formData?.cost}
              onChange={(e) => { updateField({ name: e.target.name, value: validateFloat(e.target.value) }) }}
              disabled={disabled}
              autoComplete='off'
            />
            <label htmlFor="cost">Value*</label>
          </div>
          <div className="clearfix"></div>
        </div>

        <div className="col-sm-3 pr-0">
          <div className="form-material form-container">
            <input
              className="form-control"
              name="cashEquivalent"
              id="cashEquivalent"
              value={formData?.cashEquivalent}
              onChange={(e) => { updateField({ name: e.target.name, value: validateFloat(e.target.value) }) }}
              disabled={disabled}
              autoComplete='off'
            />
            <label htmlFor="cashEquivalent">Cash Equivalent*</label>
          </div>
        </div>
      </div>
      {formData?.type && formData?.type != 'Other' &&
        <div className="col-lg-12 col-sm-12 form-section">
          <div className='col-lg-12 section-header'>
            <h1>
              {formData?.type} Details
            </h1>
          </div>

          {formData?.type === 'Vehicle' &&
            <>
              <div className="col-sm-3 pr-0">
                <div className="form-material form-container">
                  <input
                    className="form-control form-input"
                    id="numberPlate"
                    name="numberPlate"
                    value={formData?.numberPlate}
                    onChange={(e) => {
                      updateField({ name: e.target.name, value: e.target.value })
                    }}
                    disabled={disabled}
                    autoComplete='off'
                  />

                  <label htmlFor="numberPlate">Number Plate</label>
                </div>
              </div>

              <div className="col-sm-3 pr-0">
                <div className="form-material form-container">
                  <input
                    className="form-control form-input"
                    id="make"
                    name="make"
                    value={formData?.make}
                    onChange={(e) => {
                      updateField({ name: e.target.name, value: e.target.value })
                    }}
                    disabled={disabled}
                    autoComplete='off'
                  />
                  <label htmlFor="make">Make*</label>
                </div>
              </div>

              <div className="col-sm-3 pr-0">
                <div className="form-material form-container">
                  <input
                    className="form-control form-input"

                    id="model"
                    name="model"
                    value={formData?.model}
                    onChange={(e) => {
                      updateField({ name: e.target.name, value: e.target.value })
                    }}
                    disabled={disabled}
                    autoComplete='off'
                  />
                  <label htmlFor="model">Model*</label>
                </div>
              </div>

              <div className="col-sm-3 pr-0">
                <div className="form-material form-container form-container">
                  <DatePicker
                    dateFormat="dd/MM/yyyy"
                    className="form-control form-input"
                    wrapperClassName="width-full"
                    minDate={subYears(new Date(), 100)}
                    maxDate={addYears(new Date(), 100)}
                    autoComplete="off"
                    required
                    name="registeredDate"
                    id="registeredDate"
                    selected={formData?.registeredDate ? new Date(formData.registeredDate) : null}
                    onChange={(selected) => {
                      updateField({ name: 'registeredDate', value: selected })
                    }}
                  />
                  <label htmlFor="registeredDate">
                    Date First Registered*
                  </label>
                </div>
              </div>

              <div className="col-sm-3 pr-0">
                <div className="form-material form-container">
                  <label htmlFor="engineType">Engine Size*</label>
                  <Select
                    isDisabled={disabled}
                    id="engineType"
                    options={engineTypes}
                    value={engineTypes?.find(type => type.value === formData.engineType)}
                    onChange={(selected) => {
                      updateField({ name: 'engineType', value: selected.value })
                    }}
                  />
                </div>
              </div>

              <div className="col-sm-3 pr-0" style={{ paddingRight: 0 }}>
                <div className="form-material form-container">
                  <label htmlFor="fuelType">
                    Fuel Type*
                  </label>
                  <Select
                    isDisabled={disabled || formData?.fuelType === 'Pure Electric'}
                    id="fuelType"
                    name="fuelType"
                    options={fuelTypes}
                    value={fuelTypes?.find(type => type.value === (formData?.fuelType === 'Pure Electric' ? 'A' : formData?.fuelType))}
                    onChange={(selected) => {
                      updateField({ name: 'fuelType', value: selected.value })
                    }}
                  />
                </div>
              </div>

              <div className="col-sm-3 pr-0">
                <div className="form-material form-container">
                  <input
                    className="form-control form-input"
                    id="co2Emissions"
                    name="co2Emissions"
                    value={formData?.co2Emissions}
                    onChange={(e) => { updateField({ name: e.target.name, value: validateFloat(e.target.value) }) }}
                    disabled={disabled}
                    autoComplete='off'
                  />
                  <label htmlFor="co2Emissions">CO2 Emissions</label>
                </div>
              </div>

              <div className="col-sm-3" style={{ paddingRight: 0 }}>
                <div className="form-material form-container">
                  <input
                    className="form-control form-input"
                    name="zeroEmissionMilage"
                    value={formData?.zeroEmissionMileage}
                    onChange={(e) => { updateField({ name: e.target.name, value: validateFloat(e.target.value) }) }}
                    disabled={disabled}
                    autoComplete='off'
                  />
                  <label htmlFor="zeroEmissionMilage">
                    Zero Emission Mileage
                  </label>
                </div>
              </div>

              <div className='col-lg-12 col-sm-12 section-header'>
                <h1>
                  Free Fuel
                </h1>
              </div>

              <div className="col-sm-3 pr-0">
                <div className="form-material form-container">
                  <input
                    className="form-control form-input"
                    id="freeFuel[provided]"
                    name="freeFuel[provided]"
                    value={formData?.freeFuel?.provided}
                  onChange={(e) => { updateField({ subfield: 'freeFuel', name: 'provided', value: validateFloat(e.target.value) }) }}
                    disabled={disabled}
                    autoComplete='off'
                  />
                  <label htmlFor="freeFuel[provided]">Provided</label>
                </div>
              </div>
              <div className="col-sm-3 pr-0">
                <div className="form-material form-container">
                  <input
                    className="form-control form-input"
                    id="freeFuel[cashEquivalent]"
                    name="freeFuel[cashEquivalent]"
                    value={formData?.freeFuel?.cashEquivalent}
                  onChange={(e) => { updateField({ subfield: 'freeFuel', name: 'cashEquivalent', value: validateFloat(e.target.value) }) }}
                    disabled={disabled}
                    autoComplete='off'
                  />
                  <label htmlFor="freeFuel[cashEquivalent]">Cash Equivalent</label>
                </div>
              </div>
              <div className="col-sm-3 pr-0">
                <div className="form-material form-container">
                  <input
                    className="form-control form-input"
                    id="freeFuel[withdrawn]"
                    name="freeFuel[withdrawn]"
                    value={formData?.freeFuel?.withdrawn}
                  onChange={(e) => { updateField({ subfield: 'freeFuel', name: 'withdrawn', value: validateFloat(e.target.value) }) }}
                    disabled={disabled}
                    autoComplete='off'
                  />
                  <label htmlFor="freeFuel[withdrawn]">Withdrawn</label>
                </div>
              </div>
            </>
          }
          {formData?.type == 'Living Accommodation' &&
            <div className="col-sm-12">
              <div className="form-material form-container">
                <input
                  className="form-control"
                  id="location"
                  name="location"
                  value={formData?.location}
                  onChange={(e) => {
                    updateField({ name: e.target.name, value: e.target.value })
                  }}
                  disabled={disabled}
                  autoComplete='off'
                />
                <label htmlFor="location">
                  Accommodation Address
                </label>
              </div>
            </div>
          }

          {/* <div className="col-sm-6" style={{ paddingRight: 0 }}>
                  <div className="form-material form-container">
                    <Controller
                      as={
                        <DatePicker
                          dateFormat="dd MMM yyyy"
                          className="form-control"
                          calendarClassName="test123"
                        />
                      }
                      
                      name="availabilityDate"
                      valueName="selected"
                      value={
                        asset &&
                        asset.availabilityDate &&
                        new Date(asset.availablilityDate)
                      }
                      onChange={([selected]) => {
                        return selected;
                      }}
                    />
                    <label htmlFor="material-text">Car Availablity Date</label>
                  </div>
                </div> */}

          {/* <div className="col-sm-3">
              <div className="form-material form-container">
                <input
                  className="form-control"
                  
                  name="cost"
                  value={formData?.cost}
                                    disabled={disabled}
autoComplete='off'
                />
                <label htmlFor="material-text">Asset Cost</label>
              </div>
              <div className="clearfix"></div>
            </div>

            <div className="col-sm-3">
              <div className="form-material form-container">
                <input
                  className="form-control"
                  
                  name="value"
                  value={formData?.value}
                                    disabled={disabled}
autoComplete='off'
                />
                <label htmlFor="material-text">Asset Value</label>
              </div>
              <div className="clearfix"></div>
            </div>
            <div className="form-group">
              <div
                className="col-sm-3"
                style={{ paddingRight: 0 }}
              >
                <div className="form-material form-container">
                  <input
                    className="form-control"
                    
                    name="originalPrice"
                    value={formData?.originalPrice}
                                        disabled={disabled}
autoComplete='off'
                  />
                  <label htmlFor="material-text">
                    Original Price of Accommodation
                  </label>
                </div>
                <div className="clearfix"></div>
              </div>

              <div
                className="col-sm-3"
                style={{ paddingRight: 0 }}
              >
                <div className="form-material form-container">
                  <input
                    className="form-control"
                    
                    name="currentCost"
                    value={formData?.currentCost}
                                        disabled={disabled}
autoComplete='off'
                  />
                  <label htmlFor="material-text">
                    Current Cost of Accommodation
                  </label>
                </div>
                <div className="clearfix"></div>
              </div>

              <div
                className="col-sm-6"
               
              >
                <div className="form-material form-container">
                  <input
                    className="form-control"
                    
                    name="councilTax"
                    value={formData?.councilTax}
                                        disabled={disabled}
autoComplete='off'
                  />
                  <label htmlFor="material-text">
                    Council Tax on Accommodation
                  </label>
                </div>
                <div className="clearfix"></div>
              </div>

              <div
                className="col-sm-6"
                style={{ paddingRight: 0 }}
              >
                <div className="form-material form-container">
                  <input
                    className="form-control"
                    
                    name="interestRate"
                    value={formData?.interestRate}
                                        disabled={disabled}
autoComplete='off'
                  />
                  <label htmlFor="material-text">
                    Interest Rate on Accommodation
                  </label>
                </div>
                <div className="clearfix"></div>
              </div>
              <div className="clearfix"></div>


              <div
                className="col-sm-6"
               
              >
                <div className="form-material form-container">
                  <input
                    className="form-control"
                    
                    name="waterSewageCharges"
                    value={formData?.waterSewageCharges}
                                        disabled={disabled}
autoComplete='off'
                  />
                  <label htmlFor="material-text">
                    Water and Sewage Charges
                  </label>
                </div>
                <div className="clearfix"></div>
              </div>

              <div
                className="col-sm-6"
                style={{ paddingRight: 0 }}
              >
                <div className="form-material form-container">
                  <input
                    className="form-control"
                    
                    name="heatLightClean"
                    value={formData?.heatLightClean}
                                        disabled={disabled}
autoComplete='off'
                  />
                  <label htmlFor="material-text">
                    Heating, Lighting and Cleaning Charges
                  </label>
                </div>
                <div className="clearfix"></div>
              </div>

              <div
                className="col-sm-6"
               
              >
                <div className="form-material form-container">
                  <input
                    className="form-control"
                    
                    name="repMainDec"
                    value={formData?.repMainDec}
                                        disabled={disabled}
autoComplete='off'
                  />
                  <label htmlFor="material-text">
                    Maintenance and Decor Charges
                  </label>
                </div>
              </div>
              <div className="clearfix"></div>

            </div> */}

          <div className="clearfix"></div>
        </div>
      }
      {!disabled &&
        <div className="col-lg-12 col-sm-12 pr-0">
          <div className="block buttons">
            <button
              className="btn btn-success pull-right"
              type="submit"
            >
              {saving ?
                <Spinner
                  style={{ height: "1.5em", float: "left", marginRight: 5, float: 'left' }}
                  className="animate-spin -ml-1 mr-3 h-5 w-5" />
                : <CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
              }
              <div style={{ float: "left" }}>{formData?.id ? 'Update' : 'Save'} Asset</div>
            </button>
          </div>
        </div>
      }
    </form >
  );
};

const mapStateToProps = (state, ownProps) => {
  //   const { employeeDetails, agreementInfo } = state.employee;

  return {
    asset: state.asset?.item,
    saving: state.asset?.saving
  };
};

const mapDispatchToProps = (dispatch) => ({
  saveAsset: (input) => dispatch(ASSETACTIONS.saveAsset(input)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssetDetails);
